import Text from 'components/Text'
import React from 'react'
import styled, { css } from 'styled-components'
import { margin, MarginProps } from 'styled-system'

interface RadioStrokeProps {
  disabled?: boolean
  selected?: boolean
  isError?: boolean
}

interface RadioBoxComponentWrapper {
  isString: boolean
}

interface RadioBoxGroup extends MarginProps {
  disabled?: boolean
}

export interface RadioProps extends RadioStrokeProps, MarginProps {
  value?: string
  onChange?: () => void
  component?: string | React.ReactNode
  isNoTab?: boolean
  tabIndex?: number
  error?: string
  hint?: string
}

const RadioStroke = styled.div<RadioStrokeProps>`
  background-color: ${(p) => (p.disabled ? p.theme.colors.gray1 : p.theme.colors.gray3)};
  box-sizing: border-box;
  min-height: 18px;
  min-width: 18px;
  height: 18px;
  width: 18px;
  border: 1px solid
    ${(p) =>
      p.disabled
        ? p.selected
          ? p.theme.colors.blue2
          : p.theme.colors.gray3
        : p.selected
        ? p.theme.colors.blue1
        : p.theme.colors.gray4};
  ${(p) => p.isError && `border-color: ${p.theme.colors.red};`}
  border-radius: 50%;
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: ${(p) => p.theme.colors.blue2};
  }

  &:focus {
    box-shadow: 1px -1px 12px -3px ${(p) => p.theme.colors.gray5}, -1px 1px 12px -3px ${(p) => p.theme.colors.gray5};
    border-color: ${(p) => (p.selected ? p.theme.colors.blue4 : p.theme.colors.gray5)};
    outline: 0 solid transparent;
  }
`

const RadioOval = styled.div<RadioStrokeProps>`
  height: ${(p) => (p.selected ? '12px' : '17px')};
  width: ${(p) => (p.selected ? '12px' : '17px')};
  cursor: pointer;
  border-radius: 50%;
  background-color: ${(p) =>
    p.disabled ? (p.selected ? '#36f' : p.theme.colors.blue2) : p.selected ? '#36f' : p.theme.colors.gray4};
  border-color: ${(p) => (p.isError ? p.theme.colors.red : p.theme.colors.gray4)};
  opacity: ${(p) => (p.disabled ? '48%' : '1.0')};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  &:hover {
    background-color: ${(p) => (p.selected ? p.theme.colors.blue2 : p.theme.colors.blue1)};
    opacity: ${(p) => (p.selected ? '1.0' : '16%')};
  }

  &:focus: {
    background-color: ${(p) => (p.selected ? p.theme.colors.blue1 : p.theme.colors.gray5)};
    opacity: 40%;
  }
`

const RadioBoxGroup = styled.div<RadioBoxGroup>`
  ${margin}
  display: flex;
  align-items: center;
  justify-content: center;

  :focus {
    outline: none;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
    `}
`

const RadioBoxComponentWrapper = styled.div<RadioBoxComponentWrapper>`
  flex-grow: 1;
  margin-left: 8px;
  ${(p) => p.isString && 'cursor: pointer;'}
`

const Radio: React.FC<RadioProps> = ({ tabIndex, onChange, ...props }) => (
  <>
    <RadioBoxGroup {...props}>
      <RadioStroke
        disabled={props.disabled}
        onClick={onChange}
        selected={props.selected}
        tabIndex={props.isNoTab ? -1 : tabIndex || 0}
        isError={!!props.error || props.isError}
      >
        {props.selected && (
          <RadioOval disabled={props.disabled} selected={props.selected} isError={!!props.error || props.isError} />
        )}
      </RadioStroke>
      {props.component && (
        <RadioBoxComponentWrapper isString={typeof props.component === 'string'} onClick={onChange}>
          {props.component}
        </RadioBoxComponentWrapper>
      )}
    </RadioBoxGroup>
    {props.hint && <Text color="disabledText">{props.hint}</Text>}
  </>
)

export default Radio
